import { flowRight } from 'lodash';
import React, { PureComponent } from 'react';
import styled, { withTheme } from 'styled-components';
import { VirtualVisitIcon } from '@zocdoc-frontend-common/assets-icons';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    withCustomMetrics,
    MetricsClickable,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { navy } from '@zocdoc/branding/colors';
import BookButton from './BookButton';
import DoctorLocation from './DoctorLocation';
import DoctorLocationDistance from './DoctorLocationDistance';
import DoctorName from './DoctorName';
import DoctorPhoto from './DoctorPhoto';
import DoctorRating from './DoctorRating';
import VirtualVisitMessage from './VirtualVisitMessage';

/**
 * @description "Lightweight" Doctor Card (not for search!) that links to a doctor's profilefor profile, booking, etc.
 * @param {string} className
 * @param {number} index
 * @param {boolean} isMobileOrTablet
 * @param {{
 *     address1: string,
 *     address2: string,
 *     address3: string,
 *     city: string,
 *     isvirtual: boolean,
 *     state: string,
 *     zipCode: string,
 * }} location
 * @param {{
 *     averageRating: number,
 *     distance: number,
 *     firstName: string,
 *     frontEndCirclePictureUrl: string,
 *     id: string,
 *     index: number,
 *     lastName: string,
 *     optOutOfRatings: boolean,
 *     profileUrl: string,
 *     relevantSpecialty: {
 *         id: string,
 *         name: string,
 *     },
 *     reviewCount: number,
 *     title: string,
 * }} provider
 * @param {React.Ref} ref
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @returns {JSX.Element}
 */
export class BaseLightweightDoctorCard extends PureComponent {
    static defaultProps = {
        index: null,
    };

    componentDidMount() {
        const { sendMetric, provider, location, index } = this.props;

        sendMetric({
            action: 'LightweightDoctorCard Loaded',
            element: 'LightweightDoctorCard',
            eventDetails: {
                ProviderId: provider.id,
                Index: index != null ? index + 1 : undefined,
                IsVirtual: location.isVirtual,
            },
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.LOAD,
        });
    }

    render() {
        const {
            className,
            distance,
            provider,
            location,
            isMobileOrTablet,
            ref,
        } = this.props;

        const { isVirtual } = location;

        const { profileUrl, relevantSpecialty = {} } = provider;

        return (
            <MetricsClickable
                action="Click LightweightDoctorCard"
                className={className}
            >
                <LightweightDoctorCardContainer
                    ref={ref}
                    data-test="lightweight-doctor-card-container"
                    href={profileUrl}
                    target="_blank"
                >
                    <DoctorInfoTopSection>
                        <DoctorImageWrapper>
                            <StyledDoctorPhoto
                                data-test="lightweight-doctor-card-image"
                                provider={provider}
                                size="100%"
                            />
                            {isVirtual && <StyledVirtualVisit />}
                        </DoctorImageWrapper>
                        <DoctorInfo>
                            <DoctorInfoTopRow>
                                <DoctorName provider={provider} />
                                {isVirtual ? (
                                    <VirtualVisitMessage
                                        isMobileOrTablet={isMobileOrTablet}
                                    />
                                ) : (
                                    <DoctorLocationDistance
                                        distance={distance}
                                    />
                                )}
                            </DoctorInfoTopRow>
                            <DoctorSpecialty>
                                <span>{relevantSpecialty.name}</span>
                            </DoctorSpecialty>
                            {!isVirtual && (
                                <DoctorLocation location={location} />
                            )}
                            <DoctorRating provider={provider} />
                        </DoctorInfo>
                    </DoctorInfoTopSection>
                    <BookButton />
                </LightweightDoctorCardContainer>
            </MetricsClickable>
        );
    }
}

const LightweightDoctorCardContainer = styled.a`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    padding: 15px 15px 12px 15px;
    box-sizing: border-box;
`;

const DoctorInfoTopSection = styled.div`
    display: flex;
    margin-bottom: 18px;
`;

const DoctorImageWrapper = styled.div`
    width: 64px;
    height: 64px;
    position: relative;
    margin-bottom: 4px;
    ${breakpoint.small} {
        margin-bottom: 10px;
    }
`;

const StyledDoctorPhoto = styled(DoctorPhoto)`
    position: relative;
    ${breakpoint.small} {
        max-height: 150px;
        max-width: 150px;
    }
`;

const StyledVirtualVisit = styled(VirtualVisitIcon)`
    position: absolute;
    font-size: 24px;
    bottom: 0;
    right: 0;
`;

const DoctorInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-left: 23px;
`;

const DoctorInfoTopRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
`;

const DoctorSpecialty = styled.div`
    color: ${navy};
    font: 13px/1 ${FontFamily.SharpSansSemibold};
    margin-top: 4px;
    ${breakpoint.small} {
        font-size: 12px;
    }
    ${breakpoint.medium} {
        font-size: 15px;
        line-height: 1.333;
    }
`;

const LightweightDoctorCard = flowRight(
    withTheme,
    withCustomMetrics({
        component: 'LightweightDoctorCard',
    })
)(BaseLightweightDoctorCard);

export {
    BookButton,
    DoctorLocation,
    DoctorLocationDistance,
    DoctorName,
    DoctorPhoto,
    DoctorRating,
    LightweightDoctorCard,
    LightweightDoctorCard as default,
};
